import { permissions } from "Consts/permissions";
import { mediaStorageTabs } from "Modules/files/constants/mediaStorageTabs";
import { appRoutes } from "Public/consts/appRoutes";
import { checkPermission } from "Public/store/utils/checkPermission";
import {
  parseRewardsWonStat,
  parseRewardsLeaderboardStat,
  parseRewardsStatSum,
  parseSocialMessagesStat,
  parseSocialNotificationsStat,
  parseSocialGroupsStat,
  parseSocialUserStat,
  parseSocialExpertStat,
  parseSocialFriendRequestsStat,
  parseSocialStatSum,
  parseApproveStatSum,
  parseApproveProStatSum,
  parseContentReportsStat,
} from "PublicHelpers/Stat/User/parseStat";
import i18n from "Shared/plugins/i18n";

export const rawNavigationItems = (user) => {
  const { isUser, isExpert, isProvider, isTemplate } = user;
  return [
    {
      title: i18n.t("library"),
      icon: "mdi-bookshelf",
      hasAccess: hasLibraryAccess(user),
      key: ["library/", "modules/", "module/", "step-templates"],
      subItems: [
        {
          title: i18n.t("my"),
          route: appRoutes.libraryMy,
          hasAccess: checkPermission(permissions.module.join),
        },
        {
          title: i18n.t("join"),
          route: appRoutes.libraryJoin,
          hasAccess: checkPermission(permissions.module.join),
        },
        {
          title: i18n.t("my"),
          route: appRoutes.libraryMyExpertProvider,
          hasAccess: user.isExpert || user.isProvider || user.isValidator,
        },
        {
          title: i18n.t("createdByOthers"),
          route: appRoutes.libraryCreatedByOthers,
          hasAccess: user.isProvider,
        },
        {
          title: i18n.t("createdByMe"),
          route: appRoutes.libraryCreatedByMe,
          hasAccess: user.isExpert && !user.isProvider,
        },
        {
          title: i18n.t("templates"),
          route: appRoutes.libraryTemplates,
          hasAccess:
            checkPermission(permissions.module.useTemplate) || isTemplate,
        },
        {
          title: i18n.t("stepTemplates"),
          route: appRoutes.libraryStepTemplates,
          hasAccess:
            checkPermission(permissions.module.useTemplate) || isTemplate,
        },
        {
          title: i18n.t("archive"),
          route: appRoutes.libraryArchive,
          hasAccess: checkPermission(permissions.module.archive),
        },
      ],
    },
    {
      title: i18n.t("social.title"),
      icon: "mdi-forum",
      key: ["social/", "groups/", "group/create"],
      hasAccess: true,
      stat: parseSocialStatSum(user),
      subItems: [
        {
          title: i18n.t("friends"),
          hasAccess: checkPermission(permissions.social.friends),
          stat: parseSocialFriendRequestsStat(user),
          route: appRoutes.friends,
        },
        {
          title: i18n.t("users"),
          hasAccess: checkPermission(permissions.social.users),
          route: appRoutes.users,
          stat: parseSocialUserStat(user),
        },
        {
          title: i18n.t("experts"),
          hasAccess: checkPermission(permissions.social.experts),
          stat: parseSocialExpertStat(user),
          route: appRoutes.experts,
        },
        {
          title: i18n.t("groups"),
          hasAccess: checkPermission(permissions.group.listing),
          stat: parseSocialGroupsStat(user),
          route: appRoutes.groups,
        },
        {
          title: i18n.t("messages"),
          hasAccess: checkPermission(permissions.conversation.listing),
          stat: parseSocialMessagesStat(user),
          route: appRoutes.messages,
        },
        {
          title: i18n.t("notifications"),
          hasAccess: checkPermission(permissions.notification.listing),
          stat: parseSocialNotificationsStat(user),
          route: appRoutes.notifications,
        },
      ],
    },
    {
      title: i18n.t("todo"),
      icon: "mdi-check-circle",
      key: ["to-do/", `${appRoutes.contentReports.replace("/", "")}`],
      hasAccess: hasApproveAccess(),
      stat: parseApproveStatSum(user),
      proStat: parseApproveProStatSum(user),
      contentReportStat: parseContentReportsStat(user),
      subItems: [
        {
          title: i18n.t("users"),
          hasAccess: checkPermission(permissions.approve.users),
          route: appRoutes.approveUsers,
        },
        {
          title: i18n.t("groups"),
          hasAccess: checkPermission(permissions.approve.groups),
          route: appRoutes.approveGroups,
        },
        {
          title: i18n.t("modules"),
          hasAccess: checkPermission(permissions.approve.modules),
          route: appRoutes.approveModules,
        },
        {
          title: i18n.t("steps"),
          hasAccess: checkPermission(permissions.approve.steps),
          route: appRoutes.approveSteps,
        },
        {
          title: i18n.t("contentReports"),
          hasAccess: checkPermission(permissions.contentReport.review),
          stat: parseContentReportsStat(user),
          statColor: "red",
          route: appRoutes.contentReports,
        },
      ],
    },
    {
      title: i18n.t("reports"),
      hasAccess: !user.isValidator && hasReportsAccess(),
      key: ["traqing/", "/notes"],
      icon: "mdi-finance",
      subItems: [
        {
          title: i18n.t("reports"),
          route: appRoutes.traqingExpertProvider,
          hasAccess: checkPermission(permissions.reports.management) && !isUser,
        },
        {
          title: i18n.t("status"),
          route: appRoutes.traqingStatus,
          hasAccess: checkPermission(permissions.reports.userReports) && isUser,
        },
        {
          title: i18n.t("reports"),
          route: appRoutes.traqingReports,
          hasAccess: checkPermission(permissions.reports.userReports) && isUser,
        },
        {
          title: i18n.t("notes"),
          route: appRoutes.providerExpertNotes,
          hasAccess:
            (isExpert || isProvider) && checkPermission(permissions.note.index),
        },
        {
          title: i18n.t("notes"),
          route: appRoutes.traqingNotes(),
          hasAccess: isUser && checkPermission(permissions.note.index),
        },
      ],
    },
    {
      title: i18n.t("rewards"),
      icon: "mdi-trophy",
      key: "reward/",
      stat: parseRewardsStatSum(user),
      hasAccess: isUser,
      subItems: [
        {
          title: i18n.t("potential"),
          hasAccess: isUser,
          route: appRoutes.rewardsPotential,
        },
        {
          title: i18n.t("won"),
          hasAccess: isUser,
          stat: parseRewardsWonStat(user),
          route: appRoutes.rewardsWon,
        },
        {
          title: i18n.t("leaderboard"),
          hasAccess: isUser,
          stat: parseRewardsLeaderboardStat(user),
          route: appRoutes.rewardsLeaderboard,
        },
      ],
    },
    {
      title: i18n.t("media"),
      icon: "mdi-image-area",
      key: "media-storage/",
      hasAccess: isExpert || isProvider,
      subItems: [
        {
          title: mediaStorageTabs.all.text,
          hasAccess: isExpert || isProvider,
          route: appRoutes.mediaStorage(mediaStorageTabs.all.value),
        },
        {
          title: mediaStorageTabs.stepResources.text,
          hasAccess: isExpert || isProvider,
          route: appRoutes.mediaStorage(mediaStorageTabs.stepResources.value),
        },
        {
          title: mediaStorageTabs.stepProof.text,
          hasAccess: isExpert || isProvider,
          route: appRoutes.mediaStorage(mediaStorageTabs.stepProof.value),
        },
      ],
    },
  ];
};

const hasApproveAccess = () =>
  checkPermission(permissions.approve.users) ||
  checkPermission(permissions.approve.groups) ||
  checkPermission(permissions.approve.modules) ||
  checkPermission(permissions.approve.steps);

const hasReportsAccess = () => {
  const { userReports, management } = permissions.reports;
  return checkPermission(userReports) || checkPermission(management);
};

const hasLibraryAccess = (user) => {
  const { isUser } = user;
  if (isUser && !checkPermission(permissions.module.join)) return false;
  return true;
};
